import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Box,
  Typography,
  IconButton,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useSnackbar } from '../../components/SnackbarProvider';
import axiosInstance from '../../lib/axios';
import type { Supplier, CreateSupplierDto, UpdateSupplierDto, PaymentTerms } from '../../types';

interface SupplierFormModalProps {
  open: boolean;
  onClose: () => void;
  supplier?: Supplier | null;
}

const paymentTermsOptions: { value: PaymentTerms; label: string }[] = [
  { value: 'due_on_receipt', label: 'Paiement immédiat' },
  { value: 'net_7', label: 'Net 7 jours' },
  { value: 'net_15', label: 'Net 15 jours' },
  { value: 'net_30', label: 'Net 30 jours' },
  { value: 'net_60', label: 'Net 60 jours' },
  { value: 'net_90', label: 'Net 90 jours' },
  { value: 'custom', label: 'Personnalisé' },
];

interface FormData {
  name: string;
  code: string;
  contactName: string;
  phone: string;
  email: string;
  address: string;
  defaultPaymentTerms: PaymentTerms;
  currency: string;
  taxId: string;
  notes: string;
  isActive: boolean;
}

const defaultValues: FormData = {
  name: '',
  code: '',
  contactName: '',
  phone: '',
  email: '',
  address: '',
  defaultPaymentTerms: 'net_30',
  currency: 'DZD',
  taxId: '',
  notes: '',
  isActive: true,
};

export const SupplierFormModal = ({ open, onClose, supplier }: SupplierFormModalProps) => {
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();
  const isEditing = !!supplier;

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues,
  });

  useEffect(() => {
    if (supplier) {
      reset({
        name: supplier.name,
        code: supplier.code,
        contactName: supplier.contactName || '',
        phone: supplier.phone || '',
        email: supplier.email || '',
        address: supplier.address || '',
        defaultPaymentTerms: supplier.defaultPaymentTerms,
        currency: supplier.currency,
        taxId: supplier.taxId || '',
        notes: supplier.notes || '',
        isActive: supplier.isActive,
      });
    } else {
      reset(defaultValues);
    }
  }, [supplier, reset]);

  const createMutation = useMutation({
    mutationFn: async (data: CreateSupplierDto) => {
      const response = await axiosInstance.post('/suppliers', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      showMessage('Fournisseur créé avec succès', 'success');
      onClose();
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Erreur lors de la création';
      showMessage(message, 'error');
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: UpdateSupplierDto) => {
      const response = await axiosInstance.patch(`/suppliers/${supplier?.id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      showMessage('Fournisseur mis à jour avec succès', 'success');
      onClose();
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Erreur lors de la mise à jour';
      showMessage(message, 'error');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      await axiosInstance.delete(`/suppliers/${supplier?.id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      showMessage('Fournisseur supprimé avec succès', 'success');
      onClose();
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Erreur lors de la suppression';
      showMessage(message, 'error');
    },
  });

  const onSubmit = async (data: FormData) => {
    // Clean up empty optional fields
    const cleanData = Object.fromEntries(
      Object.entries(data).filter(([_, v]) => v !== '')
    );

    if (isEditing) {
      await updateMutation.mutateAsync(cleanData as UpdateSupplierDto);
    } else {
      await createMutation.mutateAsync(cleanData as CreateSupplierDto);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Voulez-vous vraiment supprimer ce fournisseur ?')) {
      await deleteMutation.mutateAsync();
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {isEditing ? 'Modifier le fournisseur' : 'Nouveau fournisseur'}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent dividers>
          <Grid container spacing={3}>
            {/* Basic Information */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Informations de base
              </Typography>
            </Grid>

            <Grid item xs={12} sm={8}>
              <Controller
                name="name"
                control={control}
                rules={{ required: 'Le nom est requis' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Nom du fournisseur"
                    fullWidth
                    required
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    disabled={isLoading}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <Controller
                name="code"
                control={control}
                rules={{
                  required: 'Le code est requis',
                  pattern: {
                    value: /^[A-Z0-9-]+$/,
                    message: 'Majuscules, chiffres et tirets uniquement',
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Code"
                    fullWidth
                    required
                    placeholder="SUPP-001"
                    error={!!errors.code}
                    helperText={errors.code?.message}
                    disabled={isLoading}
                    onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                  />
                )}
              />
            </Grid>

            {/* Contact Information */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                Contact
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="contactName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Nom du contact"
                    fullWidth
                    disabled={isLoading}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="phone"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Téléphone"
                    fullWidth
                    placeholder="+213..."
                    disabled={isLoading}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="email"
                control={control}
                rules={{
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: 'Email invalide',
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Email"
                    fullWidth
                    type="email"
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    disabled={isLoading}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="address"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Adresse"
                    fullWidth
                    disabled={isLoading}
                  />
                )}
              />
            </Grid>

            {/* Payment & Financial */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                Conditions financières
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="defaultPaymentTerms"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Conditions de paiement</InputLabel>
                    <Select
                      {...field}
                      label="Conditions de paiement"
                      disabled={isLoading}
                    >
                      {paymentTermsOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <Controller
                name="currency"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Devise</InputLabel>
                    <Select
                      {...field}
                      label="Devise"
                      disabled={isLoading}
                    >
                      <MenuItem value="DZD">DZD</MenuItem>
                      <MenuItem value="EUR">EUR</MenuItem>
                      <MenuItem value="USD">USD</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <Controller
                name="taxId"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="N. fiscal"
                    fullWidth
                    disabled={isLoading}
                  />
                )}
              />
            </Grid>

            {/* Notes & Status */}
            <Grid item xs={12}>
              <Controller
                name="notes"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Notes"
                    fullWidth
                    multiline
                    rows={3}
                    disabled={isLoading}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={field.value}
                        onChange={field.onChange}
                        disabled={isLoading}
                      />
                    }
                    label="Fournisseur actif"
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          {isEditing && (
            <Button
              color="error"
              onClick={handleDelete}
              disabled={isLoading}
              sx={{ mr: 'auto' }}
            >
              Supprimer
            </Button>
          )}
          <Button onClick={onClose} disabled={isLoading}>
            Annuler
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={16} /> : null}
          >
            {isEditing ? 'Mettre à jour' : 'Créer'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
